import React, { useEffect, useState, useMemo } from "react";

const STAR_COLORS = [
  "#FFB3D9", "#D4B8FF", "#FFF0F7", "#A8EDEA", "#F0E6FF",
  "#FFFFFF", "#E8E8F5", "#C8C8E0", "#FFFFFF", "#E0E0F0",
  "#FFB3D9", "#D4B8FF",
];

const makeStars = (count) =>
  Array.from({ length: count }, (_, i) => ({
    id:      i,
    top:     `${Math.random() * 100}%`,
    left:    `${Math.random() * 100}%`,
    size:    `${0.7 + Math.random() * 2.4}px`,
    color:   STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)],
    dur:     `${1.2 + Math.random() * 3.0}s`,
    delay:   `${Math.random() * 4}s`,
    opacity: 0.12 + Math.random() * 0.78,
  }));

const TAGLINE = "Engineer by training. Communicator by choice. Curious by default.";

const STATS = [
  { value: 5,  label: "roles",     suffix: "+" },
  { value: 25, label: "certs",     suffix: "" },
  { value: 6,  label: "languages", suffix: "" },
  { value: 3,  label: "countries", suffix: "" },
];

// step 0 → nothing
// step 1 → stars + hero (portrait + name + badge)
// step 2 → tagline (typewriter)
// step 3 → stats (count-up)
// step 4 → enter button
const STEP_TIMINGS = [250, 1350, 2500, 3800];

// ── Count-up hook ───────────────────────────────────────────────────────────
const useCountUp = (target, duration, active) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    setValue(0);
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 2.5);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return value;
};

// ── Typewriter hook ─────────────────────────────────────────────────────────
const useTypewriter = (text, msPerChar, active) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    if (!active) { setDisplayed(""); return; }
    let i = 0;
    setDisplayed("");
    const tick = () => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i < text.length) setTimeout(tick, msPerChar);
    };
    setTimeout(tick, msPerChar);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  return displayed;
};

// ── Single animated stat ────────────────────────────────────────────────────
const StatCounter = ({ value, label, suffix, active }) => {
  const count = useCountUp(value, 900, active);
  return (
    <div className="intro-stat">
      <span className="intro-stat__value">{count}{suffix}</span>
      <span className="intro-stat__label">{label}</span>
    </div>
  );
};

// ── Main intro ──────────────────────────────────────────────────────────────
const IntroSequence = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const stars = useMemo(() => makeStars(300), []);
  const typedTagline = useTypewriter(TAGLINE, 21, step >= 2);

  useEffect(() => {
    const timers = STEP_TIMINGS.map((t, i) =>
      setTimeout(() => setStep(i + 1), t)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="intro-overlay">

      {/* Skip */}
      <button className="intro-skip-btn" onClick={onComplete}>skip →</button>

      {/* Twinkling star field */}
      {stars.map((s) => (
        <span
          key={s.id}
          className="intro-star"
          style={{
            top: s.top, left: s.left,
            width: s.size, height: s.size,
            background: s.color,
            opacity: step >= 1 ? s.opacity : 0,
            "--dur":   s.dur,
            "--delay": s.delay,
            boxShadow: `0 0 ${parseFloat(s.size) * 2.5}px ${s.color}`,
            transition: "opacity 1.8s ease",
          }}
        />
      ))}

      {/* Deep-space nebula veil */}
      <div
        className="intro-nebula-veil"
        style={{ opacity: step >= 1 ? 1 : 0, transition: "opacity 2.5s ease" }}
      />

      {/* Main content */}
      <div className="intro-content">

        {/* ── Phase 1: Portrait + Name + Badge ── */}
        <div
          className="intro-hero"
          style={{
            opacity:   step >= 1 ? 1 : 0,
            transform: step >= 1 ? "scale(1) translateY(0)" : "scale(0.86) translateY(32px)",
            transition: "opacity 1.1s cubic-bezier(0.22,1,0.36,1), transform 1.1s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="intro-portrait-wrap">
            <div className="intro-portrait-glow" />
            <div className="intro-portrait-ring" />
            <div className="intro-portrait-ring intro-portrait-ring--outer" />
            <img
              src="/ela3.jpg"
              alt="Ela Rebai"
              className="intro-portrait-img"
            />
          </div>

          <h1 className="intro-title">
            {"Ela Rebai".split("").map((char, i) => (
              <span
                key={i}
                className="intro-title-char"
                style={{
                  animationDelay: `${i * 0.07}s`,
                  display: char === " " ? "inline-block" : undefined,
                  width:   char === " " ? "0.35em"        : undefined,
                }}
              >
                {char}
              </span>
            ))}
          </h1>

          <div className="intro-role-badge">
            <span className="intro-role-dot" />
            Software Engineer · Data &amp; AI
          </div>
        </div>

        {/* ── Divider ── */}
        {step >= 2 && <div className="intro-divider" />}

        {/* ── Phase 2: Typewriter tagline ── */}
        {step >= 2 && (
          <p className="intro-subtitle">
            {typedTagline}
            <span className="intro-cursor">|</span>
          </p>
        )}

        {/* ── Phase 3: Stats with count-up ── */}
        {step >= 3 && (
          <div className="intro-stats" style={{ animation: "slide-up 0.55s ease both" }}>
            {STATS.map((s, i) => (
              <StatCounter
                key={i}
                value={s.value}
                label={s.label}
                suffix={s.suffix}
                active={step >= 3}
              />
            ))}
          </div>
        )}

        {/* ── Phase 4: Enter CTA ── */}
        {step >= 4 && (
          <div className="intro-cta-wrap">
            <button className="intro-enter-btn" onClick={onComplete}>
              <span>Explore the work</span>
              <span className="intro-btn-arrow">→</span>
            </button>
            <p className="intro-cta-hint">Click any planet to open a section</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default IntroSequence;
